import { toast } from "react-toastify";
import { useEffect, useState } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { useCommercialReceptionReason } from "@/features/commercialRegistrationReceptionBookFeed/hooks/useCommercialReceptionReason";
import {
  getSearchMaxDate,
  getSearchMinDate,
  getStartAndEndDate,
} from "@/features/commercialRegistrationReceptionBookFeed/utils/dateTime";
import { type ParseResult } from "papaparse";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useApiClient } from "@/hooks/useApiClient";
import { HttpStatusCode, isAxiosError } from "axios";
import { PagePaper, SectionPaper } from "@/components/Paper";
import { Box, Grid, Link, Typography, Stack, Divider } from "@mui/material";
import { ReadOnlyTooltip } from "@/components/Tooltip/ReadOnlyTooltip";
import { PageTitle } from "@/components/Title";
import { FileUploadButton } from "@/features/commercialRegistrationReceptionBookFeed/components/FileUploadButton";
import { CommercialMonitoringReceptionReasonSelect } from "@/features/commercialRegistrationMonitoring/components/CommercialMonitoringReceptionReasonSelect";
import { CDateRangePicker } from "@/components/DatePicker/CDateRangePicker";
import { Button, ButtonVariantOption, SubmitButton } from "@/components/Button";
import { KeyboardArrowLeft } from "@mui/icons-material";
import SavedSearchIcon from "@mui/icons-material/SavedSearch";
import { CTextFieldIdentifyName } from "@/components/TextField/CTextFieldIdentifyName";
import { SizingWrapperStyle } from "@/components/Wrapper";
import { type PostCommercialMonitoringFileAPIResponse } from "../types";
import { PostCommercialMonitoringFromFileAPI } from "../api";
import { CustomModal } from "@/components/Modal";
import { CCheckbox } from "@/components/Checkbox/CCheckbox";
import { Checkbox } from "@/components/Checkbox/Checkbox";
import { SendToTrackingService } from "@/api/handlers/errors";
import { Label } from "@/components/Label";
import { UserConfigGetAPI } from "@/features/accountSettings/api/UserConfigAPI";
import { useCommercialMonitoringAmount } from "../hooks/useCommercialMonitoringAmount";
import { calculateTermMonth } from "../utils/calculateTermMonth";
import { calculateLimitMonth } from "../utils/calculateLimitMonth";
import { distinctCommercialCsvRows } from "../utils/distinctCommercialCsvRows";
import { getFileExtension, readFile } from "@/utils/readFile";
import { useReadOnlyUser } from "@/hooks/useReadOnlyUser";

const ONCE_UPDATE_MAX_CSV_ROWS = 1_000;
const MONTHLY_UPDATE_MAX_CSV_ROWS = 5_000;
const CORPORATE_VALUE_SECURITY_INTEREST_LABEL = "企価設";
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB
const ALLOWED_FILE_EXTENSIONS = ["csv", "xlsx", "xls"];

/**
 * 受けとったCSVファイルの要素の件数が指定された件数を超えているか判定する
 * @param content papaparseのCSVパース結果オブジェクト
 * @param count 最大値
 * @return 最大値を超えているならtrue、それ以外はfalse
 */
const isLineCountExceeded = (
  content: ParseResult<unknown>,
  count: number
): boolean => {
  return content.data.length > count;
};

export const CommercialMonitoringUpload: React.FC = () => {
  // ユーザコンフィグを取得するフック
  const userConfig = UserConfigGetAPI();

  // アップロード対象のファイルオブジェクト
  const [fileToUpload, setFileToUpload] = useState<File | null>(null);
  const [rowCount, setRowCount] = useState<number>(0);

  // MAX_CSV_ROWS件越えエラーメッセージ関連のState
  const [showFileErrorMessage, setShowFileErrorMessage] =
    useState<boolean>(false);

  // 料金がかかる旨を通知するためのState
  const [fileNoticeMessage, setFileNoticeMessage] = useState<string[]>([]);

  // 実行ボタン無効化用State
  const [executeButtonDisabled, setExecuteButtonDisabled] =
    useState<boolean>(true);

  const [isOpen, setIsOpen] = useState<boolean>(false);
  // 確認ボタン押下後のキャンセルボタン無効化State
  const [isPushedConfirmButton, setIsPushedConfirmButton] =
    useState<boolean>(false);

  // 登記要因セレクト項目
  const { receptionReasonOptions } = useCommercialReceptionReason();

  // モニタリングに関する金額関連
  const { data: amount } = useCommercialMonitoringAmount();

  // ルーティング用
  const navigate = useNavigate();

  // 法務局受付日 開始、終了の初期値
  const [startDate, endDate] = getStartAndEndDate();
  const minDate = getSearchMinDate();
  const maxDate = getSearchMaxDate();

  // React-Hook-Form用スキーマ定義
  const UploadFormDataSchema = z.object({
    identifyName: z
      .string()
      .trim()
      .min(1, { message: "識別名を入力してください" })
      .max(256, { message: "256文字まで入力可能です" }),
    legalAffairsBureauRequestDateRange: z.tuple([
      z.date().min(minDate).max(maxDate),
      z.date().min(minDate).max(maxDate),
    ]),
    notMonthlyUpdatable: z.boolean(),
    receptionReasons: z.array(z.string()).min(1, "登記原因を選択してください"),
  });

  // 定義したZodのスキーマをTypescriptの型に変換
  type UploadFormData = z.infer<typeof UploadFormDataSchema>;

  const reactHookForm = useForm<UploadFormData>({
    mode: "all",
    defaultValues: {
      identifyName: "",
      legalAffairsBureauRequestDateRange: [startDate, endDate],
      notMonthlyUpdatable: false,
      receptionReasons: [],
    },
    resolver: zodResolver(UploadFormDataSchema),
  });

  const [
    watchNotMonthlyUpdatable,
    watchLegalAffairsBureauRequestDateRange,
    watchReceptionReasons,
  ] = reactHookForm.watch([
    "notMonthlyUpdatable",
    "legalAffairsBureauRequestDateRange",
    "receptionReasons",
  ]);

  const { apiClient } = useApiClient();
  const isReadOnly = useReadOnlyUser();

  // 登記原因の選択肢のうち「企業価値担保権」に該当するもの
  const corporateValueSecurityInterestOption = receptionReasonOptions.find(
    (option) => option.label === CORPORATE_VALUE_SECURITY_INTEREST_LABEL
  );
  const hasCorporateValueSecurityInterestOption =
    corporateValueSecurityInterestOption !== undefined;

  // 表示専用の派生値: 登記原因に「企業価値担保権」が含まれているかどうかをそのままチェック状態とする
  const isFixedToCorporateValueSecurityInterest =
    corporateValueSecurityInterestOption !== undefined &&
    watchReceptionReasons.includes(corporateValueSecurityInterestOption.value);

  // 「企業価値担保権を追加する」チェックボックス操作時、登記原因に対象値を追加/除去する
  const handleToggleCorporateValueSecurityInterest = (
    checked: boolean
  ): void => {
    if (!corporateValueSecurityInterestOption) return;

    const targetValue = corporateValueSecurityInterestOption.value;
    const currentReceptionReasons = reactHookForm.getValues("receptionReasons");

    if (checked) {
      if (currentReceptionReasons.includes(targetValue)) return;
      reactHookForm.setValue(
        "receptionReasons",
        [...currentReceptionReasons, targetValue],
        { shouldValidate: true }
      );
      return;
    }

    reactHookForm.setValue(
      "receptionReasons",
      currentReceptionReasons.filter((value) => value !== targetValue),
      { shouldValidate: true }
    );
  };

  useEffect(() => {
    const legalAffairsBureauRequestDateStart =
      watchLegalAffairsBureauRequestDateRange[0];
    const legalAffairsBureauRequestDateEnd =
      watchLegalAffairsBureauRequestDateRange[1];

    if (
      !legalAffairsBureauRequestDateStart ||
      !legalAffairsBureauRequestDateEnd ||
      Number.isNaN(legalAffairsBureauRequestDateStart.getTime()) ||
      Number.isNaN(legalAffairsBureauRequestDateEnd.getTime()) ||
      legalAffairsBureauRequestDateStart > legalAffairsBureauRequestDateEnd
    ) {
      setFileNoticeMessage([]);
      setExecuteButtonDisabled(true);
      return;
    }

    if (!amount || amount.price <= 0) return;
    let termMonth = calculateTermMonth(
      legalAffairsBureauRequestDateStart,
      legalAffairsBureauRequestDateEnd
    );
    const message = [];
    const unitPrice = amount?.price; // 1件あたりのモニタリング金額
    const maximumPrice = amount?.maximum; // 1件あたりのモニタリング課金額上限
    if (rowCount) {
      if (watchNotMonthlyUpdatable) {
        const limitMonth = calculateLimitMonth(unitPrice, maximumPrice);
        if (termMonth > limitMonth) {
          termMonth = limitMonth;
        }
        const totalCost = (rowCount * termMonth * unitPrice).toLocaleString(
          "ja-JP"
        );
        message.push(`注意: 実行すると${totalCost}円の費用が発生します。`);
        message.push(
          `過去分1法人あたりの上限金額は${maximumPrice.toLocaleString(
            "ja-JP"
          )}円です。`
        );
      } else {
        const monthlyCost = (rowCount * unitPrice).toLocaleString("ja-JP");
        message.push(
          `注意: 実行すると毎月最大${monthlyCost}円の費用が発生します。`
        );
      }
    }
    setFileNoticeMessage(message);

    const isRowCountError = watchNotMonthlyUpdatable
      ? rowCount > ONCE_UPDATE_MAX_CSV_ROWS
      : rowCount > MONTHLY_UPDATE_MAX_CSV_ROWS;

    setShowFileErrorMessage(isRowCountError);
    setExecuteButtonDisabled(isRowCountError);
  }, [
    rowCount,
    watchNotMonthlyUpdatable,
    watchLegalAffairsBureauRequestDateRange,
    amount,
  ]);

  /**
   * ファイル選択時の処理
   * - エラーメッセージ削除
   * - CSVファイルまたはExcelファイルの読み込み
   * - ファイルMAX_CSV_ROWS件制限の確認
   * - ファイル重複項目（会社法人等番号）の削除
   * - エラーの場合はメッセージ表示
   * - 成功の場合は実行ボタンの有効化
   * @param event
   * @return void
   */
  // ファイル未選択・エラー時にファイル選択状態をリセットする
  const resetFileSelection = (): void => {
    setExecuteButtonDisabled(true);
    setFileToUpload(null);
    setRowCount(0);
    setFileNoticeMessage([]);
  };

  const handleFileSelected = async (
    event: React.ChangeEvent<HTMLInputElement>
  ): Promise<void> => {
    setShowFileErrorMessage(false);
    setExecuteButtonDisabled(true);
    setFileToUpload(null);

    // ファイルが選択されているときのみ確認画面へボタンを有効にする
    const files = event.target.files ?? [];
    if (files.length > 0) {
      const file = files[0];
      const extension = getFileExtension(file.name);
      if (!ALLOWED_FILE_EXTENSIONS.includes(extension)) {
        toast.error(
          "CSV または Excelファイル（.xlsx、.xls）を指定してください"
        );
        resetFileSelection();
        return;
      }
      if (file.size > MAX_FILE_SIZE) {
        toast.error("ファイルサイズは5MB以下にしてください");
        resetFileSelection();
        return;
      }

      try {
        const fileData = await readFile(file);
        const resultData = distinctCommercialCsvRows(fileData);
        setRowCount(resultData.data.length);

        if (reactHookForm.getValues("notMonthlyUpdatable")) {
          if (isLineCountExceeded(resultData, ONCE_UPDATE_MAX_CSV_ROWS)) {
            setShowFileErrorMessage(true);
            setFileNoticeMessage([]);
            return;
          }
        } else {
          if (isLineCountExceeded(resultData, MONTHLY_UPDATE_MAX_CSV_ROWS)) {
            setShowFileErrorMessage(true);
            setFileNoticeMessage([]);
            return;
          }
        }

        setExecuteButtonDisabled(false);
        setFileToUpload(file);
      } catch (error) {
        // ファイル読み込みエラーの場合
        toast.error(
          error instanceof Error
            ? error.message
            : "ファイルの読み込みに失敗しました"
        );
        resetFileSelection();
      }
    } else {
      // ファイルが選択されていない場合
      resetFileSelection();
    }
  };

  // CSVアップロードの確認ボタンが押された時の処理
  const handlePushedConfirmButton = (): void => {
    setIsOpen(true);
  };

  // 確認モーダルで「はい」を選択した時の処理
  const handleCsvUploadButtonClick = (
    _event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    cancelLoading: () => void
  ): void => {
    if (!fileToUpload || !reactHookForm.formState.isValid) {
      return;
    }

    // NOTE: 月初日を取得するために、月を1進めて1日目を指定する
    const legalAffairsBureauRequestDateStart = new Date();
    legalAffairsBureauRequestDateStart.setFullYear(
      reactHookForm
        .getValues("legalAffairsBureauRequestDateRange")[0]
        .getFullYear(),
      reactHookForm
        .getValues("legalAffairsBureauRequestDateRange")[0]
        .getMonth(),
      1
    );

    // NOTE: 月末日を取得するために、月を1進めて0日目を指定する
    const legalAffairsBureauRequestDateEnd = new Date();
    legalAffairsBureauRequestDateEnd.setFullYear(
      reactHookForm
        .getValues("legalAffairsBureauRequestDateRange")[1]
        .getFullYear(),
      reactHookForm
        .getValues("legalAffairsBureauRequestDateRange")[1]
        .getMonth() + 1,
      0
    );

    setIsPushedConfirmButton(true);
    PostCommercialMonitoringFromFileAPI(apiClient, {
      file: fileToUpload,
      name: reactHookForm.getValues("identifyName"),
      receptionReasonIds: reactHookForm.getValues("receptionReasons"),
      monthlyUpdatable: !reactHookForm.getValues("notMonthlyUpdatable"),
      legalAffairsBureauRequestDateStart,
      legalAffairsBureauRequestDateEnd,
    })
      .then((response) => {
        navigate(`/commercial-monitoring/${response.id ?? ""}`);
      })
      .catch((error) => {
        if (
          isAxiosError<PostCommercialMonitoringFileAPIResponse>(error) &&
          error.response?.status === HttpStatusCode.BadRequest
        ) {
          toast.warning(
            <>
              ファイルのアップロードに失敗しました。
              <br />
              ファイル内容をご確認ください。
            </>
          );
          return;
        }
        if (
          isAxiosError<PostCommercialMonitoringFileAPIResponse>(error) &&
          (error.response?.status ?? HttpStatusCode.InternalServerError) >=
            HttpStatusCode.InternalServerError
        ) {
          SendToTrackingService(error);
          toast.error("ファイルのアップロードに失敗しました");
        }
      })
      .finally(() => {
        cancelLoading();
        setIsPushedConfirmButton(false);
      });
  };

  return (
    <PagePaper>
      <form>
        {/* ページタイトル */}
        <Box sx={{ display: "flex", mb: 0 }}>
          <SavedSearchIcon sx={{ mr: 1 }} fontSize="large" />
          <PageTitle>商業登記データアップロード</PageTitle>
        </Box>
        <Stack spacing={1} sx={{ ml: 5, mb: 3, width: "fit-content" }}>
          <Link
            href={
              "/assets/sample_R.E.DATA商業登記受付帳モニタリングサンプル.csv"
            }
          >
            見本CSVをダウンロード
          </Link>
          <Link
            href={
              "/assets/sample_R.E.DATA商業登記受付帳モニタリングサンプル.xlsx"
            }
          >
            見本Excelをダウンロード
          </Link>
        </Stack>

        <SectionPaper>
          <Box sx={{ m: 3 }}>
            <Grid container justifyContent="center">
              <Grid item xs={6}>
                <Grid container direction="column" spacing={4}>
                  <Grid item>
                    <Label required={true}>識別名</Label>
                    <CTextFieldIdentifyName
                      name={"identifyName"}
                      control={reactHookForm.control}
                      size={SizingWrapperStyle.LARGE}
                    />
                  </Grid>
                  <Grid item>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Label required={true}>登記原因</Label>
                      {hasCorporateValueSecurityInterestOption && (
                        <Checkbox
                          checked={isFixedToCorporateValueSecurityInterest}
                          onChange={handleToggleCorporateValueSecurityInterest}
                          label={"企業価値担保権を追加する"}
                        />
                      )}
                    </Box>
                    <CommercialMonitoringReceptionReasonSelect
                      value={watchReceptionReasons}
                      onChange={(value) => {
                        reactHookForm.setValue("receptionReasons", value, {
                          shouldValidate: true,
                        });
                      }}
                      options={receptionReasonOptions}
                    />
                  </Grid>
                  <Grid item>
                    <CCheckbox<UploadFormData>
                      name={"notMonthlyUpdatable"}
                      control={reactHookForm.control}
                      label={"月次でモニタリングしない"}
                    />
                  </Grid>
                  <Grid item>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: { xs: "column", sm: "row" },
                      }}
                    >
                      <CDateRangePicker<UploadFormData>
                        name={"legalAffairsBureauRequestDateRange"}
                        control={reactHookForm.control}
                        startDateLabel="法務局受付日 開始"
                        endDateLabel="法務局受付日 終了"
                        minDate={minDate}
                        maxDate={maxDate}
                        defaultCalendarMonth={startDate}
                        disabled={!watchNotMonthlyUpdatable}
                        views={["year", "month"]}
                        inputFormat={"yyyy/MM"}
                        mask={"____/__"}
                        displayDateLabel={true}
                        requiredStart={watchNotMonthlyUpdatable}
                        requiredEnd={watchNotMonthlyUpdatable}
                      />
                    </Box>
                  </Grid>

                  <Grid item>
                    <Label required={true}>
                      CSVまたはエクセルをアップロードする
                    </Label>
                    <Box display={"flex"} justifyContent="start" my={2} gap={3}>
                      <FileUploadButton
                        variant={ButtonVariantOption.Outlined}
                        onChange={(event) => {
                          (async (): Promise<void> => {
                            await handleFileSelected(event);
                          })();
                        }}
                        accept={".csv, .xlsx, .xls"}
                        label={"アップロード"}
                      />
                      <Typography variant="body1" sx={{ lineHeight: 2 }}>
                        {fileToUpload
                          ? fileToUpload?.name
                          : "選択されていません"}
                      </Typography>
                    </Box>
                    <Typography variant="caption" sx={{ lineHeight: 2 }}>
                      アップロードボタンを押してファイルを選択してください
                    </Typography>
                  </Grid>

                  {showFileErrorMessage && (
                    <Grid item>
                      <Typography textAlign={"left"} color={"error"}>
                        {`ファイルの項目数は${(watchNotMonthlyUpdatable
                          ? ONCE_UPDATE_MAX_CSV_ROWS
                          : MONTHLY_UPDATE_MAX_CSV_ROWS
                        ).toLocaleString(
                          "ja-JP"
                        )}以下にしてください。現在の項目数: ${rowCount.toLocaleString(
                          "ja-JP"
                        )}件`}
                      </Typography>
                    </Grid>
                  )}

                  {fileNoticeMessage.length > 0 &&
                    !showFileErrorMessage &&
                    userConfig !== undefined &&
                    !userConfig.isPriceHidden && (
                      <Grid item>
                        <Typography textAlign={"left"} color={"error"}>
                          {fileNoticeMessage.map((message, idx) => (
                            <div key={idx}>{message}</div>
                          ))}
                          会社法人等番号が重複する行を除いた概算金額です。実際の金額はアップロード後に確定します。
                        </Typography>
                      </Grid>
                    )}
                </Grid>
              </Grid>
            </Grid>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <Grid item mb={2}>
            <Box display="flex" justifyContent="center" alignItems="center">
              <ReadOnlyTooltip isReadOnly={isReadOnly}>
                <Button
                  label={"実行する"}
                  disabled={
                    isReadOnly ||
                    !reactHookForm.formState.isValid ||
                    executeButtonDisabled ||
                    !fileToUpload
                  }
                  onClick={handlePushedConfirmButton}
                  variant={ButtonVariantOption.Contained}
                />
              </ReadOnlyTooltip>
            </Box>
          </Grid>
        </SectionPaper>

        {/* 戻る */}
        <Box sx={{ mt: 2 }}>
          <Link
            component={RouterLink}
            to={"/commercial-monitoring"}
            sx={{ display: "inline-block" }}
          >
            <Box sx={{ display: "flex" }}>
              <KeyboardArrowLeft />
              <Typography>商業登記モニタリング画面に戻る</Typography>
            </Box>
          </Link>
        </Box>
      </form>
      {/* 課金確認モーダル */}
      <CustomModal
        isOpen={isOpen}
        handleClose={(
          event: Record<string, unknown>,
          reason: "backdropClick" | "escapeKeyDown"
        ): void => {
          if (
            reason &&
            (reason === "backdropClick" || reason === "escapeKeyDown")
          ) {
            return;
          }
          setIsOpen(false);
        }}
        ariaLabelledby="confirm-modal"
        ariaDescribedby="confirm-modal-description"
      >
        <PageTitle>確認</PageTitle>
        <Box>
          <Typography>課金が発生します。よろしいですか？</Typography>

          <Box
            sx={{
              mr: 2,
              my: 3,
              display: "flex",
              justifyContent: "center",
              gap: 2,
            }}
          >
            <Button
              label="いいえ"
              disabled={isPushedConfirmButton}
              onClick={() => {
                setIsOpen(false);
              }}
              variant={ButtonVariantOption.Outlined}
            />
            <ReadOnlyTooltip isReadOnly={isReadOnly}>
              <SubmitButton
                label={"はい"}
                timeout={120_000} // ms
                onClick={handleCsvUploadButtonClick}
                variant={ButtonVariantOption.Contained}
                disabled={isReadOnly}
              />
            </ReadOnlyTooltip>
          </Box>
        </Box>
      </CustomModal>
    </PagePaper>
  );
};
